// services/entity-search.service.ts
import { api } from '@/modules/shared/lib/axios'
import type { ResponseAPI } from '@/modules/shared/types/response-api.type'
import type { Entity } from '@/modules/shared/types/entity.type'

export type EntitySearchPayload = {
  entityType: string
  filter: string
  inputValue: string
  dataInclusao?: string
}

export type EntitySearchResponse = {
  entities: Entity[]
  totalRecords: number
  hasMore: boolean
}

class EntitySearchService {
  private readonly ITEMS_PER_PAGE = 15

  private buildBaseUrl(payload: EntitySearchPayload): string {
    const baseUrl = `entidade/${payload.entityType}/${payload.filter}/${payload.inputValue}`
    return payload.dataInclusao && payload.entityType == '1' ? `${baseUrl}/${payload.dataInclusao}` : baseUrl
  }

  async getTotalRecords(payload: EntitySearchPayload): Promise<number> {
    try {
      const url = this.buildBaseUrl(payload)
      const response = await api.get(url)

      if (response.data?.body) {
        const parsed = JSON.parse(response.data.body)
        return parsed.records || 0
      }
      return 0
    } catch (error) {
      console.error('Erro ao buscar total de registros:', error)
      throw error
    }
  }

  async getEntitiesPage(payload: EntitySearchPayload, page: number): Promise<Entity[]> {
    try {
			const url = this.buildBaseUrl(payload)
			const offset = (page - 1) * this.ITEMS_PER_PAGE
			const response = await api.get(`${url}?offset=${offset}&limit=${this.ITEMS_PER_PAGE}`)

			if (response.data?.body) {
				const entities = JSON.parse(response.data.body)
        return Array.isArray(entities) ? entities : []
      }
      return []
    } catch (error) {
      console.error('Erro ao buscar entidades:', error)
      throw error
    }
  }

  async searchInitialEntities(payload: EntitySearchPayload): Promise<EntitySearchResponse> {
    const totalRecords = await this.getTotalRecords(payload)
    console.log('📊 Total de registros encontrados:', totalRecords)

    if (totalRecords === 0) {
      return { entities: [], totalRecords: 0, hasMore: false }
    }

    const entities = await this.getEntitiesPage(payload, 1)
    const hasMore = entities.length === this.ITEMS_PER_PAGE && totalRecords > this.ITEMS_PER_PAGE

    console.log('🔍 Busca inicial:', {
      entitiesLoaded: entities.length,
      totalRecords,
      hasMore,
      itemsPerPage: this.ITEMS_PER_PAGE
    })

    return { entities, totalRecords, hasMore }
  }

  async loadMoreEntities(payload: EntitySearchPayload, page: number, currentTotal: number, totalRecords: number): Promise<{ entities: Entity[], hasMore: boolean }> {
    console.log('📥 Carregando mais entidades:', {
      page,
      currentTotal,
      totalRecords,
      shouldLoad: currentTotal < totalRecords
    })

    if (currentTotal >= totalRecords) {
      console.log('🛑 Todos os registros já foram carregados')
      return { entities: [], hasMore: false }
    }

    const entities = await this.getEntitiesPage(payload, page)
    const newTotal = currentTotal + entities.length
    const hasMore = entities.length === this.ITEMS_PER_PAGE && newTotal < totalRecords

    console.log('✅ Mais entidades carregadas:', {
      newEntities: entities.length,
      newTotal,
      totalRecords,
      hasMore
    })

    return { entities, hasMore }
  }
}

export const entitySearchService = new EntitySearchService()